import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReviewViewModel } from "../../configuration/api/apiSchema";
import ReviewItem from "./ReviewItem";

interface ReviewsListProps {
  reviews: ReviewViewModel[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRespond?: (reviewId: string, response: string) => void;
  showActions?: boolean;
  emptyMessage?: string;
  title?: string;
  currentPage: number;
}

const ReviewsList: React.FC<ReviewsListProps> = ({
  reviews,
  loading,
  error,
  totalCount,
  onPageChange,
  onRespond,
  showActions = false,
  emptyMessage = "No reviews found",
  title = "Reviews",
  currentPage,
}) => {
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenResponseDialog = (reviewId: string) => {
    setSelectedReviewId(reviewId);
    setResponseText("");
    setResponseDialogOpen(true);
  };

  const handleCloseResponseDialog = () => {
    setResponseDialogOpen(false);
    setSelectedReviewId(null);
  };

  const handleSubmitResponse = async () => {
    if (!selectedReviewId || !responseText.trim() || !onRespond) return;
    
    setIsSubmitting(true);
    try {
      await onRespond(selectedReviewId, responseText);
      handleCloseResponseDialog();
    } catch (error) {
      console.error("Failed to submit response:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageSize = 5;
  const pageCount = Math.ceil(totalCount / pageSize);

  return (
    <Box>
      {title && (
        <Typography variant="h5" gutterBottom fontWeight="medium">
          {title}
        </Typography>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : reviews.length === 0 ? (
        <Fade in={true} timeout={500}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
            bgcolor="#f5f5f5"
            borderRadius={2}
          >
            <Typography color="text.secondary">{emptyMessage}</Typography>
          </Box>
        </Fade>
      ) : (
        <Fade in={true} timeout={500}>
          <Box>
            {reviews.map((review) => (
              <ReviewItem
                key={review.id}
                review={review}
                onRespond={handleOpenResponseDialog}
                showActions={showActions}
              />
            ))}

            {pageCount > 1 && (
              <Box display="flex" justifyContent="center" mt={4}>
                <Pagination
                  count={pageCount}
                  page={currentPage}
                  onChange={(_, page) => onPageChange(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Box>
        </Fade>
      )}

      {/* Response Dialog */}
      <Dialog
        open={responseDialogOpen}
        onClose={handleCloseResponseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Respond to Review</Typography>
            <IconButton onClick={handleCloseResponseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Your Response"
            fullWidth
            multiline
            rows={4}
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseResponseDialog} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitResponse} 
            variant="contained" 
            color="primary"
            disabled={isSubmitting || !responseText.trim()}
          >
            {isSubmitting ? "Submitting..." : "Submit Response"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewsList; 